import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Scale, ArrowLeft, FileText, AlertTriangle, CheckCircle,
  AlertCircle, RefreshCw, BookOpen, ExternalLink, HelpCircle, X,
  GitCompare, Eye, Edit3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { AuthContext } from "@/App";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContractReview = () => {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { user } = useContext(AuthContext);
  
  const [document, setDocument] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Redline state
  const [redlineAnalysis, setRedlineAnalysis] = useState(null);
  const [redlineLoading, setRedlineLoading] = useState(false);
  const [viewMode, setViewMode] = useState("original"); // original, redline, side-by-side
  
  // Explain clause state
  const [selectedText, setSelectedText] = useState("");
  const [explainOpen, setExplainOpen] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState(null);

  useEffect(() => {
    fetchDocument();
  }, [documentId]);

  const fetchDocument = async () => {
    try {
      const docRes = await axios.get(`${BACKEND_URL}/api/documents/${documentId}`);
      setDocument(docRes.data);
      
      // Try to fetch existing review
      try {
        const reviewRes = await axios.get(`${BACKEND_URL}/api/contract-reviews/${documentId}`);
        setReview(reviewRes.data);
      } catch (e) {
        // No existing review
      }
      
      // Try to fetch existing redline analysis
      try {
        const redlineRes = await axios.get(`${BACKEND_URL}/api/redline/${documentId}`);
        setRedlineAnalysis(redlineRes.data);
      } catch (e) {
        // No existing redline
      }
    } catch (error) {
      console.error("Error fetching document:", error);
      toast.error("Failed to load document");
    } finally {
      setLoading(false);
    }
  };

  const startReview = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/contract-review`, {
        document_id: documentId,
        matter_id: document?.matter_id || "",
        review_type: "full"
      });
      setReview(res.data);
      toast.success("Contract analysis complete");
    } catch (error) {
      console.error("Review error:", error);
      toast.error("Failed to analyze contract");
    } finally {
      setAnalyzing(false);
    }
  };

  const generateRedline = async () => {
    setRedlineLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/redline/analyze`, {
        document_id: documentId,
        party_perspective: "buyer"
      });
      setRedlineAnalysis(res.data);
      setViewMode("redline");
      toast.success(`Found ${res.data.total_issues} potential issues`);
    } catch (error) {
      console.error("Redline error:", error);
      toast.error("Failed to generate redline analysis");
    } finally {
      setRedlineLoading(false);
    }
  };

  const handleExplainClause = async () => {
    if (!selectedText.trim()) {
      toast.error("Please enter or select text to explain");
      return;
    }
    
    setExplaining(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/explain-clause`, {
        clause_text: selectedText,
        document_context: document?.content?.substring(0, 2000),
        contract_type: document?.doc_type
      });
      
      if (res.data.success) {
        setExplanation(res.data);
        toast.success("Clause explained");
      } else {
        toast.error("Failed to explain clause");
      }
    } catch (error) {
      console.error("Explain error:", error);
      toast.error("Failed to explain clause");
    } finally {
      setExplaining(false);
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (text && text.length > 10) {
      setSelectedText(text);
      setExplainOpen(true);
      setExplanation(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="contract-review-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/documents")}
              data-testid="back-btn"
              className="text-[#94A3B8] hover:text-[#E2E8F0]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Scale className="w-6 h-6 text-[#C5A059]" />
              <span className="font-playfair text-lg font-semibold text-[#E2E8F0]">Contract Review</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
              <TabsList className="bg-[#15171C] border border-white/10 rounded-sm">
                <TabsTrigger 
                  value="original"
                  data-testid="view-original"
                  className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059] rounded-sm text-xs uppercase tracking-wide"
                >
                  <Eye className="w-3 h-3 mr-1" /> Original
                </TabsTrigger>
                <TabsTrigger 
                  value="redline"
                  data-testid="view-redline"
                  className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059] rounded-sm text-xs uppercase tracking-wide"
                  disabled={!redlineAnalysis}
                >
                  <GitCompare className="w-3 h-3 mr-1" /> Redline
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            {!redlineAnalysis && (
              <Button 
                onClick={generateRedline}
                disabled={redlineLoading}
                data-testid="generate-redline-btn"
                className="bg-[#4A6FA5] text-white hover:bg-[#5A7FB5] rounded-sm uppercase text-xs tracking-wide"
              >
                {redlineLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...
                  </>
                ) : (
                  <>
                    <GitCompare className="w-4 h-4 mr-2" /> Generate Redline
                  </>
                )}
              </Button>
            )}
            
            {!review && (
              <Button 
                onClick={startReview}
                disabled={analyzing}
                data-testid="start-review-btn"
                className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm uppercase text-xs tracking-wide"
              >
                {analyzing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" /> Analyze Contract
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content - Two Pane Layout */}
      <div className="flex h-[calc(100vh-65px)]">
        {/* Left Pane - Document */}
        <div className="flex-1 border-r border-white/10 flex flex-col">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h2 className="font-playfair text-lg text-[#E2E8F0]">{document?.filename}</h2>
              <div className="flex items-center gap-3 mt-2">
                <Badge variant="outline" className="text-[#C5A059] border-[#C5A059]/30 font-mono text-xs">
                  {document?.doc_type}
                </Badge>
                <span className="text-[#64748B] font-mono text-xs">
                  {(document?.file_size / 1024).toFixed(1)} KB
                </span>
                {redlineAnalysis && (
                  <Badge variant="outline" className="text-[#10B981] border-[#10B981]/30 font-mono text-xs">
                    {redlineAnalysis.total_issues} issues found
                  </Badge>
                )}
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={() => setExplainOpen(true)}
              data-testid="explain-clause-btn"
              className="border-white/20 text-[#E2E8F0] hover:bg-white/5 rounded-sm text-xs"
            >
              <HelpCircle className="w-4 h-4 mr-1" /> Explain Clause
            </Button>
          </div>
          
          <ScrollArea className="flex-1 p-6">
            <div 
              className="bg-[#15171C] rounded-sm p-6 border border-white/5"
              onMouseUp={handleTextSelection}
            >
              <p className="text-[#64748B] text-xs mb-3 font-mono">TIP: Select text to explain it</p>
              
              {viewMode === "original" ? (
                <pre className="text-[#E2E8F0] font-mono text-sm whitespace-pre-wrap leading-relaxed select-text cursor-text">
                  {document?.content || "No content available for preview."}
                </pre>
              ) : viewMode === "redline" && redlineAnalysis ? (
                <div>
                  {/* Redline Legend */}
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm bg-[#EF4444]/20 border border-[#EF4444]"></span>
                      <span className="text-[#94A3B8] text-xs font-mono">High Risk</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm bg-[#F59E0B]/20 border border-[#F59E0B]"></span>
                      <span className="text-[#94A3B8] text-xs font-mono">Medium Risk</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm bg-[#3B82F6]/20 border border-[#3B82F6]"></span>
                      <span className="text-[#94A3B8] text-xs font-mono">Low Risk</span>
                    </div>
                  </div>
                  
                  {/* Redline HTML Content */}
                  <div 
                    className="text-[#E2E8F0] font-mono text-sm whitespace-pre-wrap leading-relaxed select-text cursor-text redline-view"
                    dangerouslySetInnerHTML={{ __html: redlineAnalysis.redline_html }}
                  />
                  
                  {/* Redline Styles */}
                  <style>{`
                    .redline-view .redline-issue {
                      position: relative;
                      cursor: pointer;
                      border-radius: 2px;
                      padding: 2px 4px;
                    }
                    .redline-view .severity-high {
                      background-color: rgba(239, 68, 68, 0.2);
                      border-bottom: 2px solid #EF4444;
                    }
                    .redline-view .severity-medium {
                      background-color: rgba(245, 158, 11, 0.2);
                      border-bottom: 2px solid #F59E0B;
                    }
                    .redline-view .severity-low {
                      background-color: rgba(59, 130, 246, 0.2);
                      border-bottom: 2px solid #3B82F6;
                    }
                    .redline-view .redline-tooltip {
                      display: none;
                      position: absolute;
                      bottom: 100%;
                      left: 0;
                      background: #15171C;
                      border: 1px solid #C5A059;
                      padding: 8px 12px;
                      border-radius: 4px;
                      font-size: 12px;
                      color: #E2E8F0;
                      max-width: 300px;
                      z-index: 100;
                      white-space: normal;
                    }
                    .redline-view .redline-issue:hover .redline-tooltip {
                      display: block;
                    }
                  `}</style>
                </div>
              ) : (
                <pre className="text-[#E2E8F0] font-mono text-sm whitespace-pre-wrap leading-relaxed select-text cursor-text">
                  {document?.content || "No content available for preview."}
                </pre>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Right Pane - Analysis */}
        <div className="w-[480px] bg-[#15171C] flex flex-col">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="font-playfair text-lg text-[#E2E8F0]">Analysis</h2>
          </div>
          
          <ScrollArea className="flex-1">
            {/* Redline Summary */}
            {redlineAnalysis && (
              <div className="p-6 border-b border-white/10">
                <h3 className="font-playfair text-lg text-[#E2E8F0] mb-4">Redline Analysis</h3>
                
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <Card className="bg-[#EF4444]/10 border-[#EF4444]/30 rounded-sm p-3 text-center">
                    <p className="text-[#EF4444] text-2xl font-mono font-bold">{redlineAnalysis.high_priority}</p>
                    <p className="text-[#94A3B8] text-xs font-mono">High</p>
                  </Card>
                  <Card className="bg-[#F59E0B]/10 border-[#F59E0B]/30 rounded-sm p-3 text-center">
                    <p className="text-[#F59E0B] text-2xl font-mono font-bold">{redlineAnalysis.medium_priority}</p>
                    <p className="text-[#94A3B8] text-xs font-mono">Medium</p>
                  </Card>
                  <Card className="bg-[#3B82F6]/10 border-[#3B82F6]/30 rounded-sm p-3 text-center">
                    <p className="text-[#3B82F6] text-2xl font-mono font-bold">{redlineAnalysis.low_priority}</p>
                    <p className="text-[#94A3B8] text-xs font-mono">Low</p>
                  </Card>
                </div>
                
                <p className="text-[#94A3B8] font-manrope text-sm">{redlineAnalysis.summary}</p>
                
                {/* Individual Changes */}
                {redlineAnalysis.changes && redlineAnalysis.changes.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <h4 className="text-[#E2E8F0] font-mono text-xs uppercase">Issues Found</h4>
                    {redlineAnalysis.changes.slice(0, 10).map((change, idx) => (
                      <RedlineChangeCard key={idx} change={change} />
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {!review && !redlineAnalysis ? (
              <div className="p-6 text-center">
                {analyzing || redlineLoading ? (
                  <div>
                    <RefreshCw className="w-12 h-12 text-[#C5A059] mx-auto mb-4 animate-spin" />
                    <h3 className="font-playfair text-lg text-[#E2E8F0] mb-2">Analyzing Contract</h3>
                    <p className="text-[#94A3B8] font-manrope text-sm">
                      Our AI is reviewing the document for risks, issues, and recommendations...
                    </p>
                  </div>
                ) : (
                  <div>
                    <FileText className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                    <h3 className="font-playfair text-lg text-[#E2E8F0] mb-2">Ready to Analyze</h3>
                    <p className="text-[#94A3B8] font-manrope text-sm mb-4">
                      Click "Analyze Contract" for AI review or "Generate Redline" for markup suggestions.
                    </p>
                  </div>
                )}
              </div>
            ) : review && (
              <div className="p-6 space-y-6">
                {/* Status */}
                <Card className="bg-[#0B0C10] border-white/5 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981]" />
                    <div>
                      <p className="text-[#E2E8F0] font-manrope text-sm font-medium">Analysis Complete</p>
                      <p className="text-[#64748B] font-mono text-xs">
                        Reviewed on {new Date(review.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Analysis Content */}
                <div>
                  <h3 className="font-playfair text-lg text-[#E2E8F0] mb-4">Review Summary</h3>
                  <div className="bg-[#0B0C10] border border-white/5 rounded-sm p-4">
                    <div className="text-[#E2E8F0] font-manrope text-sm whitespace-pre-wrap markdown-content leading-relaxed">
                      {review.analysis}
                    </div>
                  </div>
                </div>

                {/* Issues List (if any) */}
                {review.issues && review.issues.length > 0 && (
                  <div>
                    <h3 className="font-playfair text-lg text-[#E2E8F0] mb-4">Identified Issues</h3>
                    <div className="space-y-3">
                      {review.issues.map((issue, idx) => (
                        <IssueCard key={idx} issue={issue} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
      
      {/* Explain Clause Sheet */}
      <Sheet open={explainOpen} onOpenChange={setExplainOpen}>
        <SheetContent className="bg-[#15171C] border-white/10 w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle className="font-playfair text-[#E2E8F0] flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-[#C5A059]" />
              Explain Clause
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-[#94A3B8] font-mono text-xs uppercase block mb-2">
                Clause Text
              </label>
              <Textarea
                value={selectedText}
                onChange={(e) => setSelectedText(e.target.value)}
                placeholder="Select text from the document or paste a clause here..."
                data-testid="clause-text-input"
                className="bg-[#0B0C10] border-white/10 text-[#E2E8F0] min-h-[120px] font-mono text-sm"
              />
            </div>
            
            <Button
              onClick={handleExplainClause}
              disabled={explaining || !selectedText.trim()}
              data-testid="explain-clause-submit-btn"
              className="w-full bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm"
            >
              {explaining ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...
                </>
              ) : (
                "Explain This Clause"
              )}
            </Button>
            
            {explanation && (
              <ScrollArea className="h-[400px] mt-4">
                <div className="bg-[#0B0C10] border border-white/5 rounded-sm p-4">
                  <pre className="text-[#E2E8F0] font-manrope text-sm whitespace-pre-wrap leading-relaxed markdown-content">
                    {explanation.explanation}
                  </pre>
                </div>
              </ScrollArea>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const RedlineChangeCard = ({ change }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case "high": return "text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/30";
      case "medium": return "text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/30";
      case "low": return "text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30";
      default: return "text-[#64748B] bg-[#64748B]/10 border-[#64748B]/30";
    }
  };

  return (
    <Card className="bg-[#0B0C10] border-white/5 rounded-sm p-3">
      <div className="flex items-start gap-2">
        <Badge className={`${getSeverityColor(change.severity)} font-mono text-xs uppercase shrink-0`}>
          {change.severity}
        </Badge>
        <div className="flex-1 min-w-0">
          <p className="text-[#E2E8F0] font-mono text-xs truncate">{change.original}</p>
          {change.reason && (
            <p className="text-[#94A3B8] text-xs mt-1">{change.reason}</p>
          )}
          {change.new && (
            <p className="text-[#10B981] font-mono text-xs mt-2">
              Suggestion: {change.new}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};

const IssueCard = ({ issue }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case "high": return "text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/30";
      case "medium": return "text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/30";
      case "low": return "text-[#10B981] bg-[#10B981]/10 border-[#10B981]/30";
      default: return "text-[#64748B] bg-[#64748B]/10 border-[#64748B]/30";
    }
  };

  const getIcon = (severity) => {
    switch (severity) {
      case "high": return <AlertTriangle className="w-4 h-4" />;
      case "medium": return <AlertCircle className="w-4 h-4" />;
      default: return <CheckCircle className="w-4 h-4" />;
    }
  };

  return (
    <Card className="bg-[#0B0C10] border-white/5 rounded-sm p-4">
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded-sm ${getSeverityColor(issue.severity)}`}>
          {getIcon(issue.severity)}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`${getSeverityColor(issue.severity)} font-mono text-xs uppercase`}>
              {issue.severity}
            </Badge>
            <span className="text-[#64748B] font-mono text-xs">{issue.issue_type}</span>
          </div>
          <p className="text-[#E2E8F0] font-manrope text-sm">{issue.description}</p>
          {issue.suggested_markup && (
            <div className="mt-3 p-3 bg-[#15171C] rounded-sm border border-white/5">
              <p className="text-[#64748B] font-mono text-xs uppercase mb-1">Suggested</p>
              <p className="text-[#C5A059] font-mono text-sm">{issue.suggested_markup}</p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ContractReview;
